import { type HTMLInputAutoCompleteAttribute, useId, useState } from 'react'
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form'
import { cn } from '@/lib/utils'
import { EyeIcon } from '../decorative/eye-icon'
import { EyeOffIcon } from '../decorative/eye-off-icon'
import { XIcon } from '../decorative/x-icon'
import { text } from './text'

export interface InputProps<T extends FieldValues = FieldValues>
  extends Omit<React.ComponentProps<'input'>, 'name' | 'defaultValue'>, // omit props that conflict with UseControllerProps
    UseControllerProps<T> {
  /** Label for the input */
  label: string
  /** Make `autocomplete` attribute required (but can be undefined), so we don't foget to specify it. This attribute is big UX win. */
  autoComplete: HTMLInputAutoCompleteAttribute | undefined
  /**
   * Component to be placed below the error message
   * Why is this here? Because we want to place it within the empty space (roughly 80px high) below the input
   */
  extraComponent?: React.ReactNode
  /**
   * By default, error messages only show after field has been touched or form has been submitted.
   * This prop forces the error message to show immediately on pristine form.
   *
   * Must be used together with `mode: 'onChange'` in `useForm` and `form.trigger()` on mount in the parent component.
   */
  showErrorMessageOnPristineForm?: boolean
}

export function Input<T extends FieldValues = FieldValues>({
  label,
  className,
  type,
  required,
  extraComponent,
  control,
  name,
  rules,
  defaultValue,
  shouldUnregister,
  disabled,
  showErrorMessageOnPristineForm,
  ...props
}: InputProps<T>) {
  const id = useId()
  const [showPassword, setShowPassword] = useState(false)

  const { field, fieldState, formState } = useController({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
    disabled,
  })

  // Error message only shows when field is touched or the form submitted (or on pristine form if `showErrorMessageOnPristineForm` is true)
  let errorMessage = ''
  if (showErrorMessageOnPristineForm || fieldState.isTouched || formState.isSubmitted) {
    errorMessage = String(fieldState.error?.message ?? '')
  }

  // Clear button shows when field has a value
  const showClearButton = !!field.value
  const onClear = () => field.onChange('')

  // Password input changes type depending on whether eye is open or closed
  let passwordType = type
  if (type === 'password') {
    passwordType = showPassword ? 'text' : 'password'
  }

  function renderRightButton() {
    // If input type is password, render the password toggle button
    if (type === 'password') {
      return (
        <button
          type="button"
          className="absolute right-0 top-0 h-full pl-2 pr-4 flex items-center justify-center cursor-pointer"
          tabIndex={-1}
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? <EyeOffIcon className="size-6" /> : <EyeIcon className="size-6" />}
        </button>
      )
    }

    // If input type isn't password, show clear button if field has a value
    if (type !== 'password' && showClearButton) {
      return (
        <button
          type="button"
          className="absolute right-0 top-0 h-full pl-2 pr-4 flex items-center justify-center cursor-pointer"
          tabIndex={-1}
          onClick={onClear}
        >
          <XIcon className="size-6 text-cream" />
        </button>
      )
    }
  }

  return (
    <div>
      <div>
        <label
          htmlFor={id}
          className={text({
            font: 'nbArchitekt',
            size: 12,
            color: 'cream',
            className: cn(
              'select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
              className,
            ),
          })}
        >
          {label}
          {required && '*'}
        </label>

        <div className="relative mt-2">
          <input
            id={id}
            type={passwordType}
            className={cn(
              'w-full border border-cream rounded-lg pl-4 pr-12 pt-2.5 pb-3 outline-none placeholder:text-[#71717A]',
              className,
            )}
            {...field}
            {...props}
          />

          {renderRightButton()}
        </div>
      </div>

      <div className="mt-2 min-h-20">
        {errorMessage && (
          <p className={text({ font: 'beausite', size: 12, color: 'orange', weight: 'bold' })}>
            {errorMessage}
          </p>
        )}
        {extraComponent}
      </div>
    </div>
  )
}
